import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import {
  useAssignSettingSelfServeBrandInstantAnswers,
  useSettingSelfServeBrandInstantAnswers,
} from "@faire/help-center-utils/settings/useSettingSelfServiceBrandInstantAnswers";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Clipboard } from "@faire/web--source/slate/icons/foundation/Clipboard";
import React from "react";

import { SelfServeNode, SelfServeNodes } from "../../types";
import { AddMultipleProducts } from "../instantAnswers/myCatalog-addMultipleProducts";
import { AddSingleProduct } from "../instantAnswers/myCatalog-addSingleProduct";
import { AllowedAndRestrictedProducts } from "../instantAnswers/myCatalog-allowedAndRestrictedProducts";
import { EditMultipleProducts } from "../instantAnswers/myCatalog-editMultipleProducts";
import { EditSingleProduct } from "../instantAnswers/myCatalog-editSingleProduct";
import { GetFaireToHelpAddProducts } from "../instantAnswers/myCatalog-getFaireToHelpAddProducts";
import { ImportProductsWithIntegrations } from "../instantAnswers/myCatalog-importProductsWithIntegrations";
import { InternationalPricing } from "../instantAnswers/myCatalog-internationalPricing";
import { ManageProductImages } from "../instantAnswers/myCatalog-manageProductImages";
import { OpenSizing } from "../instantAnswers/myCatalog-openSizing";
import { Preorders } from "../instantAnswers/myCatalog-preorders";
import { Prepacks } from "../instantAnswers/myCatalog-prepacks";
import { ProductTranslations } from "../instantAnswers/myCatalog-productTranslations";
import { Samples } from "../instantAnswers/myCatalog-samples";
import { TariffCodes } from "../instantAnswers/myCatalog-tariffCodes";

export const useMyCatalogBrandNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const enableSettingSelfServeBrandInstantAnswers =
    useSettingSelfServeBrandInstantAnswers();
  const assignSettingSelfServeBrandInstantAnswers =
    useAssignSettingSelfServeBrandInstantAnswers();
  const articles = useArticles();

  const commonFields: SelfServeNode = {
    id: "MyCatalog",
    label: strictLocalize({
      defaultMessage: "Product catalog",
      description: {
        text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
      },
    }),
    icon: <Clipboard fontSize="24px" color="faireBlack" />,
    articles: [
      articles.ManageMyShop.CUSTOMIZE_MY_PRODUCT_TRANSLATIONS,
      articles.ManageMyShop.CURRENCY_SPECIFIC_PRICING,
      articles.ManageMyShop.SETTING_PRODUCT_INVENTORY_LEVELS,
      articles.ManageMyShop.ARRANGE_MY_SHOP_LISTINGS,
    ],
    level: 1,
    assignSetting: assignSettingSelfServeBrandInstantAnswers,
    sidebarTitle: strictLocalize({
      defaultMessage: "Catalog issue",
      description: {
        text: "Title for displaying the issue on the order summary sidebar",
      },
    }),
  };

  if (!enableSettingSelfServeBrandInstantAnswers) {
    return {
      MyCatalog: {
        ...commonFields,
      },
    };
  }

  return {
    MyCatalog: {
      ...commonFields,
      children: [
        "AddProducts",
        "GetFaireToHelpAddProducts",
        "EditProducts",
        "ListingSettings",
        "InternationalPricing",
      ],
      shouldStopAddingDefaultOtherNodeToChildren: true,
    },
    AddProducts: {
      id: "AddProducts",
      label: strictLocalize({
        defaultMessage: "Add products",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "AddSingleProduct",
        "AddMultipleProducts",
        "ImportProductsWithIntegrations",
        "AllowedAndRestrictedProducts",
      ],
      level: 2,
    },
    GetFaireToHelpAddProducts: {
      id: "GetFaireToHelpAddProducts",
      label: strictLocalize({
        defaultMessage: "Get Faire to help add products",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: GetFaireToHelpAddProducts(strictLocalize),
      articles: [articles.ManageMyShop.SUBMIT_REQUEST_TO_ADD_OR_EDIT_PRODUCTS],
      level: 2,
    },
    EditProducts: {
      id: "EditProducts",
      label: strictLocalize({
        defaultMessage: "Edit products",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "EditSingleProduct",
        "EditMultipleProducts",
        "ManageProductImages",
      ],
      level: 2,
    },
    ListingSettings: {
      id: "ListingSettings",
      label: strictLocalize({
        defaultMessage: "Listing settings",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "Preorders",
        "OpenSizing",
        "Prepacks",
        "TariffCodes",
        "ProductTranslations",
        "Samples",
      ],
      level: 2,
    },
    InternationalPricing: {
      id: "InternationalPricing",
      label: strictLocalize({
        defaultMessage: "International pricing",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: InternationalPricing(strictLocalize),
      articles: [
        articles.ManageMyShop.CURRENCY_SPECIFIC_PRICING,
        articles.International.LOCATION_SHOP_LIVE,
      ],
      level: 2,
    },
    AddSingleProduct: {
      id: "AddSingleProduct",
      label: strictLocalize({
        defaultMessage: "Add a single product",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: AddSingleProduct(strictLocalize),
      articles: [
        articles.ManageMyShop.ADD_EDIT_PRODUCTS,
        articles.ManageMyShop.PREORDERS,
        articles.ManageMyShop.WHAT_IS_PREPACK,
        articles.ManageMyShop.PRODUCT_ATTRIBUTES_AND_DETAILS,
      ],
      level: 3,
    },
    AddMultipleProducts: {
      id: "AddMultipleProducts",
      label: strictLocalize({
        defaultMessage: "Add multiple products",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: AddMultipleProducts(strictLocalize),
      articles: [articles.ManageMyShop.ADD_EDIT_PRODUCTS],
      level: 3,
    },
    ImportProductsWithIntegrations: {
      id: "ImportProductsWithIntegrations",
      label: strictLocalize({
        defaultMessage: "Import products with integrations",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: ImportProductsWithIntegrations(strictLocalize),
      articles: [
        articles.Integration.CAN_I_INTEGRATE_WITH_SHOPIFY,
        articles.Integration.PRESTASHOP_INTEGRATION_WITH_FAIRE,
        articles.Integration.WOOCOMMERCE_INTEGRATION_WITH_FAIRE,
      ],
      level: 3,
    },
    AllowedAndRestrictedProducts: {
      id: "AllowedAndRestrictedProducts",
      label: strictLocalize({
        defaultMessage: "Allowed & restricted products",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: AllowedAndRestrictedProducts(strictLocalize),
      articles: [
        articles.ManageMyShop.ADD_EDIT_PRODUCTS,
        articles.ManageMyShop.WILL_YOU_LIST_ALL_PRODUCTS,
      ],
      level: 3,
    },
    EditSingleProduct: {
      id: "EditSingleProduct",
      label: strictLocalize({
        defaultMessage: "Edit a single product",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: EditSingleProduct(strictLocalize),
      articles: [articles.ManageMyShop.ADD_EDIT_PRODUCTS],
      level: 3,
    },
    EditMultipleProducts: {
      id: "EditMultipleProducts",
      label: strictLocalize({
        defaultMessage: "Edit multiple products",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: EditMultipleProducts(strictLocalize),
      articles: [articles.ManageMyShop.CAN_I_EDIT_PRODUCTS_IN_BULK],
      level: 3,
    },
    ManageProductImages: {
      id: "ManageProductImages",
      label: strictLocalize({
        defaultMessage: "Manage product images",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: ManageProductImages(strictLocalize),
      articles: [articles.OptimizingMyPage.FEATURE_IMAGE_GUIDELINES],
      level: 3,
    },
    Preorders: {
      id: "Preorders",
      label: strictLocalize({
        defaultMessage: "Pre-orders",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: Preorders(strictLocalize),
      articles: [
        articles.ManageMyShop.PREORDERS,
        articles.ManageMyShop.APPAREL_PREORDERS,
      ],
      level: 3,
    },
    OpenSizing: {
      id: "OpenSizing",
      label: strictLocalize({
        defaultMessage: "Open sizing",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: OpenSizing(strictLocalize),
      articles: [articles.ManageMyShop.OPEN_SIZING],
      level: 3,
    },
    Prepacks: {
      id: "Prepacks",
      label: strictLocalize({
        defaultMessage: "Prepacks",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: Prepacks(strictLocalize),
      articles: [
        articles.ManageMyShop.WHAT_IS_PREPACK,
        articles.ManageMyShop.HOW_DO_I_ADD_PRODUCTS_USING_THE_BULK_UPLOADER,
      ],
      level: 3,
    },
    TariffCodes: {
      id: "TariffCodes",
      label: strictLocalize({
        defaultMessage: "Tariff codes",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: TariffCodes(strictLocalize),
      articles: [
        articles.FulfillingOrders.SELLING_AND_IMPORTING_PRODUCTS_INTO_EU,
      ],
      level: 3,
    },
    ProductTranslations: {
      id: "ProductTranslations",
      label: strictLocalize({
        defaultMessage: "Product translations",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: ProductTranslations(strictLocalize),
      articles: [articles.ManageMyShop.CUSTOMIZE_MY_PRODUCT_TRANSLATIONS],
      level: 3,
    },
    Samples: {
      id: "Samples",
      label: strictLocalize({
        defaultMessage: "Samples",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: Samples(strictLocalize),
      articles: [articles.ManageMyShop.HOW_DO_I_OFFER_FOOD_AND_DRINK_SAMPLES],
      level: 3,
    },
  };
};
